import {
  AlertCircle,
  BarChart3,
  ExternalLink,
  Eye,
  Heart,
  MessageCircle,
  Minus,
  RefreshCw,
  Share,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAction, useQuery } from "convex/react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Id } from "~/convex/_generated/dataModel";
import { VideoEmbed } from "@/components/ui/video-embed";
import { api } from "~/convex/_generated/api";
import { useAuthActions } from "@convex-dev/auth/react";
import { useToast } from "@/components/ui/use-toast";

interface Hook {
  _id: string;
  hookText: string;
  hookCategory: string;
  hookReasoning: string;
  originalDirection?: string;
  likedAt: number;
  isFilmed?: boolean;
  videoUrl?: string;
}

interface HookAnalyticsProps {
  hook: Hook;
  creatorProfileId: Id<"exp__creatorProfile">;
}

export const HookAnalytics = ({ hook }: HookAnalyticsProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasAnalyticsError, setHasAnalyticsError] = useState(false);
  const { toast } = useToast();
  const { signIn } = useAuthActions();

  const analytics = useQuery(
    api.exp__creatorInsights.exp__getHookVideoAnalytics,
    {
      hookId: hook._id as Id<"exp__likedHooks">,
    },
  );

  const fetchVideoAnalytics = useAction(
    api.exp__creatorInsights.exp__fetchVideoAnalytics,
  );

  // Auto-fetch analytics on mount if we have a video URL but no analytics
  useEffect(() => {
    if (hook.videoUrl && !analytics && !isRefreshing && !hasAnalyticsError) {
      handleRefreshAnalytics();
    }
  }, [hook.videoUrl, analytics, hasAnalyticsError]);

  const handleRefreshAnalytics = async () => {
    if (!hook.videoUrl) return;

    setIsRefreshing(true);
    setHasAnalyticsError(false);
    try {
      await fetchVideoAnalytics({
        hookId: hook._id as Id<"exp__likedHooks">,
        videoUrl: hook.videoUrl,
      });

      toast({
        title: "Analytics Updated",
        description: "Video analytics have been refreshed successfully.",
      });
    } catch (error) {
      console.error("Error refreshing analytics:", error);
      setHasAnalyticsError(true);
      toast({
        title: "Analytics Fetch Failed",
        description:
          "Unable to retrieve video analytics. This might be due to expired social media connections.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Helper function to determine platform from video URL
  const getPlatformFromUrl = (url: string) => {
    if (url.includes("tiktok.com")) return "tiktok";
    if (url.includes("instagram.com")) return "instagram";
    if (url.includes("youtube.com") || url.includes("youtu.be"))
      return "youtube";
    return null;
  };

  // Helper function to get redirect URL for social reconnection
  const getFullRedirectUrl = () => {
    return window.location.href;
  };

  // Handle reconnecting social account
  const handleReconnectSocial = async () => {
    if (!hook.videoUrl) return;

    const platform = getPlatformFromUrl(hook.videoUrl);
    if (!platform) return;

    try {
      await signIn(platform, {
        redirectTo: getFullRedirectUrl(),
      });
    } catch (error) {
      console.error("Error reconnecting social account:", error);
      toast({
        title: "Connection Failed",
        description: `Failed to reconnect ${platform}. Please try again.`,
        variant: "destructive",
      });
    }
  };

  const formatNumber = (num?: number) => {
    if (!num) return "0";
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return "Unknown";
    return new Date(timestamp).toLocaleDateString();
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case "youtube":
      case "youtube-shorts":
        return "🎬";
      case "tiktok":
        return "🎵";
      case "instagram":
        return "📸";
      default:
        return "📱";
    }
  };

  const getEngagementRate = () => {
    if (!analytics?.views || analytics.views === 0) return 0;
    const engagements =
      (analytics.likes || 0) +
      (analytics.comments || 0) +
      (analytics.shares || 0);
    return ((engagements / analytics.views) * 100).toFixed(2);
  };

  const getTrend = (current?: number, previous?: number) => {
    if (!current || !previous) return null;
    if (current > previous) return "up";
    if (current < previous) return "down";
    return "same";
  };

  const getTrendPercentage = (current?: number, previous?: number) => {
    if (!current || !previous || previous === 0) return null;
    return (((current - previous) / previous) * 100).toFixed(1);
  };

  // Get previous analytics for trend comparison
  const previousAnalytics =
    analytics?.analyticsHistory?.[analytics.analyticsHistory.length - 2];
  const currentAnalytics =
    analytics?.analyticsHistory?.[analytics.analyticsHistory.length - 1];

  const TrendIcon = ({ trend }: { trend: string | null }) => {
    if (trend === "up")
      return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend === "down")
      return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            Video Analytics
          </h2>
          <p className="text-muted-foreground">
            Performance metrics for your filmed hook
          </p>
        </div>
      </div>

      {/* Error handling for analytics fetch failure */}
      {hasAnalyticsError && (
        <Card className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-orange-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-orange-900 dark:text-orange-100 mb-2">
                  Unable to Fetch Analytics
                </h3>
                <p className="text-sm text-orange-800 dark:text-orange-200 mb-4">
                  We couldn't retrieve analytics for this video. This usually
                  happens when your social media connection has expired or needs
                  to be refreshed.
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={handleReconnectSocial}
                    size="sm"
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    Reconnect{" "}
                    {(getPlatformFromUrl(hook.videoUrl || "") || "").replace(
                      /^./,
                      (c) => c.toUpperCase(),
                    )}
                  </Button>
                  <Button
                    onClick={handleRefreshAnalytics}
                    size="sm"
                    variant="outline"
                    disabled={isRefreshing}
                    className="border-orange-300 text-orange-700 hover:bg-orange-100 dark:border-orange-600 dark:text-orange-300"
                  >
                    <RefreshCw
                      className={`w-4 h-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
                    />
                    Try Again
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Analytics - Moved to top */}
      {analytics ? (
        <div className="space-y-6">
          {/* Analytics Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Views */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Views
                    </p>
                    <p className="text-2xl font-bold">
                      {formatNumber(analytics.views)}
                    </p>
                    {getTrend(
                      currentAnalytics?.views,
                      previousAnalytics?.views,
                    ) && (
                      <div className="flex items-center gap-1 mt-1">
                        <TrendIcon
                          trend={getTrend(
                            currentAnalytics?.views,
                            previousAnalytics?.views,
                          )}
                        />
                        <span className="text-xs text-muted-foreground">
                          {getTrendPercentage(
                            currentAnalytics?.views,
                            previousAnalytics?.views,
                          )}
                          %
                        </span>
                      </div>
                    )}
                  </div>
                  <Eye className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            {/* Likes */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Likes
                    </p>
                    <p className="text-2xl font-bold">
                      {formatNumber(analytics.likes)}
                    </p>
                    {getTrend(
                      currentAnalytics?.likes,
                      previousAnalytics?.likes,
                    ) && (
                      <div className="flex items-center gap-1 mt-1">
                        <TrendIcon
                          trend={getTrend(
                            currentAnalytics?.likes,
                            previousAnalytics?.likes,
                          )}
                        />
                        <span className="text-xs text-muted-foreground">
                          {getTrendPercentage(
                            currentAnalytics?.likes,
                            previousAnalytics?.likes,
                          )}
                          %
                        </span>
                      </div>
                    )}
                  </div>
                  <Heart className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>

            {/* Comments */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Comments
                    </p>
                    <p className="text-2xl font-bold">
                      {formatNumber(analytics.comments)}
                    </p>
                    {getTrend(
                      currentAnalytics?.comments,
                      previousAnalytics?.comments,
                    ) && (
                      <div className="flex items-center gap-1 mt-1">
                        <TrendIcon
                          trend={getTrend(
                            currentAnalytics?.comments,
                            previousAnalytics?.comments,
                          )}
                        />
                        <span className="text-xs text-muted-foreground">
                          {getTrendPercentage(
                            currentAnalytics?.comments,
                            previousAnalytics?.comments,
                          )}
                          %
                        </span>
                      </div>
                    )}
                  </div>
                  <MessageCircle className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            {/* Shares */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {analytics.platform === "instagram" ? "Shares" : "Shares"}
                    </p>
                    <p className="text-2xl font-bold">
                      {formatNumber(analytics.shares)}
                    </p>
                    {getTrend(
                      currentAnalytics?.shares,
                      previousAnalytics?.shares,
                    ) && (
                      <div className="flex items-center gap-1 mt-1">
                        <TrendIcon
                          trend={getTrend(
                            currentAnalytics?.shares,
                            previousAnalytics?.shares,
                          )}
                        />
                        <span className="text-xs text-muted-foreground">
                          {getTrendPercentage(
                            currentAnalytics?.shares,
                            previousAnalytics?.shares,
                          )}
                          %
                        </span>
                      </div>
                    )}
                  </div>
                  <Share className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Engagement Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Engagement Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {getEngagementRate()}%
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Engagement Rate
                  </p>
                </div>
                {analytics.bookmarks && (
                  <div className="text-center">
                    <p className="text-2xl font-bold text-yellow-600">
                      {formatNumber(analytics.bookmarks)}
                    </p>
                    <p className="text-sm text-muted-foreground">Bookmarks</p>
                  </div>
                )}
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {formatNumber(
                      (analytics.likes || 0) +
                        (analytics.comments || 0) +
                        (analytics.shares || 0),
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Total Engagements
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Video and Hook Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Video Embed */}
            {hook.videoUrl && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {getPlatformIcon(analytics.platform)} Video
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <VideoEmbed
                    url={hook.videoUrl}
                    platform={analytics.platform}
                    className="w-full"
                  />
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-muted-foreground">
                        Platform
                      </span>
                      <span className="text-sm capitalize">
                        {analytics.platform.replace("-", " ")}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-muted-foreground">
                        Posted
                      </span>
                      <span className="text-sm">
                        {formatDate(analytics.postDate)}
                      </span>
                    </div>
                    {analytics.title && (
                      <div>
                        <span className="text-sm font-medium text-muted-foreground">
                          Title
                        </span>
                        <p className="text-sm mt-1">{analytics.title}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Hook Information */}
            <Card>
              <CardHeader>
                <CardTitle>Original Hook</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg">
                  <p className="font-medium text-purple-900 dark:text-purple-100 mb-2">
                    {hook.hookText}
                  </p>
                  <p className="text-sm text-purple-700 dark:text-purple-300 mb-3">
                    {hook.hookReasoning}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-purple-600 dark:text-purple-400">
                    <span>Liked: {formatDate(hook.likedAt)}</span>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button
                    onClick={handleRefreshAnalytics}
                    disabled={isRefreshing || !hook.videoUrl}
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
                    />
                    Refresh Data
                  </Button>
                  {hook.videoUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(hook.videoUrl, "_blank")}
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Original
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Last Updated */}
          {analytics.lastUpdated && (
            <div className="text-center text-sm text-muted-foreground">
              Last updated: {formatDate(analytics.lastUpdated)}
            </div>
          )}
        </div>
      ) : !hook.videoUrl ? (
        <Card>
          <CardContent className="p-8 text-center">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold mb-2">No Video URL</h3>
            <p className="text-muted-foreground">
              Add a video URL to this hook to view analytics.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-8 text-center">
            <div className="animate-pulse">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold mb-2">
                Loading Analytics...
              </h3>
              <p className="text-muted-foreground">
                Fetching the latest data for your video.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
